import { useCallback, useContext, useState } from 'react'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { STUDIO_PATH } from 'Pods/ThreeDStudio/constants'
import { parseRunSlugFromPathname } from 'Pods/ThreeDStudio/Hooks/parseRunSlugFromPathname'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { clearModelSettings, setInputThreeD } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import {
  openArchiveFileChoice,
  setAcquiredSlug,
  setActiveMobilePanel,
  setIsModelSettingsOpen,
  setIsSidebarOpen,
  setIsViewerSidebarOpen,
} from 'Pods/ThreeDStudio/Store/actions/ui'
import { MOBILE_PANEL } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import useBreakpoint from 'Utils/useBreakpoint'

import {
  beginAcquiredModelOpen,
  finishAcquiredModelOpen,
  isLatestAcquiredModelOpen,
} from './acquiredModelOpenGuard'
import { PREPARE_STATUS, prepareAcquiredModelAsset } from '../Api/prepareAcquiredModelAsset'

// Keep in sync with the 0.3s width transition in _viewer_sidebar.scss. Parsing the
// model blocks the main thread, so loading must wait until the sidebar open animation
// has finished or the animation janks.
const SIDEBAR_ANIMATION_MS = 300
// Breakpoints where the workspace collapses into tabs - keep in sync with the
// media-breakpoint-down(md) rule on .mobile-tab-bar.
const MOBILE_BREAKPOINTS = ['xs', 'sm', 'md']

const waitForSidebarAnimation = () =>
  new Promise((resolve) => {
    setTimeout(resolve, SIDEBAR_ANIMATION_MS)
  })

/**
 * Opens an acquired model in the viewer: prepares the generator asset, then follows the
 * same path an upload takes (see useThreeDModelUploader) so a picked model and an
 * uploaded one land in exactly the same state - viewer only, no library card.
 * A model that only ships inside archives opens the archive file choice instead; picking a
 * file there calls openModel again with that file (`archiveFile`). Only the latest open
 * still targeting the current page lands (see acquiredModelOpenGuard).
 */
// `archiveFileChoiceEnabled` overrides the page context value, for callers rendered outside its provider.
export default function useAcquiredModelOpener({ archiveFileChoiceEnabled: enabledOverride } = {}) {
  const t = useTranslation()
  const pageContext = useContext(ThreeDStudioPageContext)
  const archiveFilesEnabled = enabledOverride ?? Boolean(pageContext.archiveFileChoiceEnabled)
  const [isOpening, setIsOpening] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const breakpoint = useBreakpoint()
  const isMobileLayout = MOBILE_BREAKPOINTS.includes(breakpoint)

  const openModel = useCallback(async (modelId, {
    targetPathname = STUDIO_PATH.GENERATE,
    archiveFile,
    preparedAsset,
    onFailure,
  } = {}) => {
    const wasViewerSidebarOpen = threeDStudioStore.getState().ui?.isViewerSidebarOpen
    const token = beginAcquiredModelOpen(targetPathname)
    // Must run before the navigation, so useModelUrlSync knows the incoming slug is a
    // purchase by the time the pathname change reaches it.
    setAcquiredSlug(parseRunSlugFromPathname(targetPathname))
    if (location?.pathname !== targetPathname) {
      navigate(targetPathname)
      clearModelSettings()
      // clearModelSettings re-opens the model settings panel for fresh-generation flows;
      // opening a purchase only shows the viewer, so keep it collapsed.
      setIsModelSettingsOpen(false)
      // clearModelSettings also force-closes the viewer sidebar. Reopen it in the same
      // synchronous tick when it was already open so it never visibly closes-then-reopens.
      if (wasViewerSidebarOpen) {
        setIsViewerSidebarOpen(true)
      }
    }
    if (isMobileLayout) {
      setIsSidebarOpen(false)
      setActiveMobilePanel(MOBILE_PANEL.VIEWER)
    }
    setIsOpening(true)

    try {
      const prepared = await prepareAcquiredModelAsset(modelId, archiveFile, {
        shouldContinue: () => isLatestAcquiredModelOpen(token),
        preparedAsset,
        archiveFilesEnabled,
      })
      if (!isLatestAcquiredModelOpen(token)) return false
      if (prepared.status === PREPARE_STATUS.CHOOSE_FILE) {
        openArchiveFileChoice({ modelId, targetPathname, candidates: prepared.candidates })
        return true
      }

      const { assetId, url, format } = prepared
      // A freshly extracted file answers without a size, so fall back to the archive listing's.
      const fileSize = prepared.fileSize ?? archiveFile?.fileSize ?? null
      const wasSidebarOpen = threeDStudioStore.getState().ui?.isViewerSidebarOpen
      setIsViewerSidebarOpen(true)
      if (!wasSidebarOpen) {
        await waitForSidebarAnimation()
      }
      if (!isLatestAcquiredModelOpen(token)) return false
      // A format taken only from the page URL may have been edited, so a load failure under it
      // says nothing about the file and must not be reported against it.
      const isAcquired = archiveFilesEnabled && Boolean(prepared.isFormatVerified)
      setInputThreeD(assetId, url, fileSize, format, { isAcquired })
      return true
    } catch {
      if (!isLatestAcquiredModelOpen(token)) return false
      window.showNotification(t('threeDModelPicker.openError'), { type: 'error', autohide: true })
      if (onFailure) {
        onFailure()
      } else if (targetPathname !== STUDIO_PATH.GENERATE) {
        // The viewer spins while the purchase slug is in the path, so a failed open with
        // nothing to retry leaves it for the studio root.
        setAcquiredSlug(null)
        navigate(STUDIO_PATH.GENERATE)
      }
      return false
    } finally {
      finishAcquiredModelOpen(token)
      setIsOpening(false)
    }
  }, [location?.pathname, navigate, isMobileLayout, t, archiveFilesEnabled])

  return { isOpening, openModel }
}
